import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Room } from 'livekit-client';  // ← add this
import StreamContainer from '../components/Streaming/StreamContainer.tsx';
//import TopBar from '../components/TopBar.jsx';
import { useAccount } from 'wagmi';
import { readContract } from '@wagmi/core'; // <-- Importar readContract
import { config } from '../config.jsx'; // <-- Importar config de wagmi
import funABI from '../config/PumpFunAbi.json'; // <-- Importación por defecto (correcta para JSON)
import { bscTestnet } from 'wagmi/chains'; // Importar la cadena necesaria o determinarla dinámicamente
import { FaSpinner } from 'react-icons/fa';
import { imageUrl, projectName, coinNames, BASE_URL } from '../utils/constants.ts'; // Importar constantes necesarias
import '../styles/StreamViewPage.css';
import { ChatOverlay } from '../components/Streaming/ChatOverlay.tsx';
import { ReactionOverlay } from '../components/Streaming/ReactionOverlay.tsx';

// minimal ERC‑20 ABI for name/symbol fallback
const ERC20_ABI = [
  { constant: true, inputs: [], name: 'name', outputs: [{ name: '', type: 'string' }], type: 'function' },
  { constant: true, inputs: [], name: 'symbol', outputs: [{ name: '', type: 'string' }], type: 'function' },
];

// Asumiendo que la chainId es fija por ahora o se puede determinar
// Idealmente, la chainId también debería ser parte de la URL o inferirse
const DEFAULT_CHAIN_ID = bscTestnet.id; // Usar bscTestnet por defecto

const StreamViewPage: React.FC = () => {
  const { tokenAddress } = useParams<{ tokenAddress: string }>();
  const location = useLocation<{ chainId?: number; ownerAddress?: string }>();
  const { address: connectedWallet } = useAccount();
  const containerRef = useRef<{ room: Room | null }>(null);

  // get from Link state
  const { chainId: stateChainId, ownerAddress: stateOwner } = location.state || {};
  const [chainId, setChainId] = useState<number>(stateChainId ?? DEFAULT_CHAIN_ID);
  const [ownerAddressFromContract, setOwnerAddressFromContract] = useState<string | undefined>(stateOwner);

  // Estados para la información del token obtenida del contrato
  const [tokenName, setTokenName] = useState<string | null>(null);
  const [tokenSymbol, setTokenSymbol] = useState<string | null>(null);
  const [loadingTokenInfo, setLoadingTokenInfo] = useState<boolean>(true);
  const [errorTokenInfo, setErrorTokenInfo] = useState<string | null>(null);

  // Estados para StreamContainer (pueden mantenerse si son necesarios)
  const [isPaused, setIsPaused] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  // useEffect para obtener información del token desde el contrato
  useEffect(() => {
    const fetchContractInfo = async () => {
      setLoadingTokenInfo(true);
      setErrorTokenInfo(null);
      try {
        console.log(`[StreamViewPage] Fetching contract info for ${tokenAddress}…`);
        const basicInfoResult = await readContract(config, {
          address: tokenAddress as any,
          abi: funABI,
          functionName: 'getFunBasicInfo',
          chainId,
        });
        console.log("[StreamViewPage] getFunBasicInfo Result:", basicInfoResult);
        if (Array.isArray(basicInfoResult) && basicInfoResult.length >= 3) {
          const [name, symbol, owner] = basicInfoResult;
          setTokenName(name);
          setTokenSymbol(symbol);
          setOwnerAddressFromContract(owner);
        } else {
          throw new Error("Unexpected result format");
        }
      } catch (err: any) {
        console.error("[StreamViewPage] getFunBasicInfo reverted:", err);
        // fallback owner from route state
        if (stateOwner) {
          console.log('[StreamViewPage] falling back to stateOwner:', stateOwner);
          setOwnerAddressFromContract(stateOwner);
        }
        // try standard ERC‑20 name/symbol
        try {
          const name = await readContract(config, {
            address: tokenAddress as any,
            abi: ERC20_ABI,
            functionName: 'name',
            chainId,
          });
          setTokenName(name as string);
        } catch {}
        try {
          const sym = await readContract(config, {
            address: tokenAddress as any,
            abi: ERC20_ABI,
            functionName: 'symbol',
            chainId,
          });
          setTokenSymbol(sym as string);
        } catch {}
      } finally {
        setLoadingTokenInfo(false);
      }
    };
    fetchContractInfo();
  }, [tokenAddress, chainId]);

  // DEBUG owner and role
  console.log('🛠️ StreamViewPage render → ownerAddressFromContract:', ownerAddressFromContract, 'connectedWallet:', connectedWallet);
  const isOwner = ownerAddressFromContract?.toLowerCase() === connectedWallet?.toLowerCase();

  const tokenLogoUrl = `${imageUrl}logo/${projectName}-${chainId}-${tokenAddress}.png`;

  return (
    <div className="stream-view-container">
     

      <div className="stream-view-main">
        {/* Left: video + owner/viewer controls */}
        <div className="stream-player-area">
          {!loadingTokenInfo && ownerAddressFromContract && (
            <StreamContainer
              ref={containerRef}
              tokenAddress={tokenAddress}
              connectedWallet={connectedWallet}
              ownerAddress={ownerAddressFromContract}
              setShowReportModal={setShowReportModal}
              showReportModal={showReportModal}
              setIsPaused={setIsPaused}
              isPaused={isPaused}
              includeChat={false}  // no chat here
            />
          )}
        </div>

        {/* Right: chat & reactions */}
        <div className="stream-sidebar">
          {containerRef.current?.room && (
            <>
              <ChatOverlay room={containerRef.current.room}/>
              <ReactionOverlay room={containerRef.current.room}/>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamViewPage;
